import React, { useState, useEffect } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
	Typography,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
	Breadcrumbs,
	Link,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Grid,
	Checkbox,
	FormControlLabel,
	makeStyles,
	TablePagination,
	TextField,
} from '@material-ui/core';
import SearchCaseForm from "../Components/SearchCase";
import instance from "api/globalaxios";
import { format } from "date-fns";
import DatePickerACCB from "../Components/DatePickerACCB";
import { displayEmail, formatPartyName, safeDateFormat, getCaseReferenceNumber, isAllowedAmountInput } from "../utils";
import { downloadTrustedUrlInIframe, navigateToTrustedUrl, validateOrigin } from "../urlSafety";
import GlobalSearch from "../Components/GlobalSearch/GlobalSearch";
import { getPreviewNotice } from "../service";
import NoticePreview from "../Components/Notice/NoticePreview";
import FormError from "../Components/FormError";
import Swal from "sweetalert2";

const NOTICE_TYPES = [
	{
		value: 'preconsent_notice_to_claimant',
		label: 'Preconsent Notice to Claimant to file Statement of Claim',
		recipient: 'claimant',
		fields: ['statementOfClaimFilingDate'],
		institutional: false,
	},
	{
		value: 'notice_to_arbitrator_for_consent',
		label: 'Letter to the Arbitrator seeking Consent (Schedule II + Disclosure Form)',
		recipient: 'arbitrator',
		fields: [],
		institutional: false,
	},
	{
		value: 'notice_to_respondent_appointment',
		label: 'Notice to Respondent intimating the Appointment of Arbitrator',
		recipient: 'respondent',
		fields: [],
		institutional: false,
	},
	{
		value: 'notice_to_respondent_written_response',
		label: 'Notice to Respondent to file Written Response / Counter Claim',
		recipient: 'respondent',
		fields: ['writtenResponseFilingDate', 'fees'],
		institutional: false,
	},
	// --- Institutional (Rule 10) notice types. The backend accepts these two
	//     values (NOTICE_INST_WRITTEN_RESPONSE / NOTICE_INST_COUNTER_CLAIM), and
	//     institutional cases only ever notice the Respondent, so these are the
	//     only two options shown for an institutional case (see
	//     noticeTypesForCategory).
	{
		value: 'notice_inst_written_response',
		label: 'Notice to Respondent to file Written Response',
		recipient: 'respondent',
		fields: ['writtenResponseFilingDate'],
		institutional: true,
	},
	{
		value: 'notice_inst_counter_claim',
		label: 'Notice to Respondent to file Statement of Objections / Counter Claim',
		recipient: 'respondent',
		fields: ['writtenResponseFilingDate', 'fees'],
		institutional: true,
	},
	// Reference (MSED) cases only. This letter to the Facilitation Council is
	// NEVER sent to a party or arbitrator — it is previewed, edited and
	// downloaded, then posted. So `downloadOnly` hides the Send button, and
	// `referenceOnly` limits it to Reference cases (see noticeTypesForCategory).
	{
		value: 'notice_msme_file_closure',
		label: 'Letter to MSME – File Closure',
		recipient: null,
		fields: [],
		institutional: false,
		referenceOnly: true,
		downloadOnly: true,
	},
];

const noticeTypeConfig = (value) =>
	NOTICE_TYPES.find((type) => type.value === value) || null;

// Institutional cases get ONLY the two institutional respondent notices; every
// other category (CMP / Reference) gets the standard notices. Reference-only
// notices (the MSME file-closure letter) are shown only on Reference cases.
const noticeTypesForCategory = (category) => {
	const cat = (category || '').toLowerCase();
	if (cat === 'institutional') return NOTICE_TYPES.filter((t) => t.institutional);
	return NOTICE_TYPES.filter(
		(t) => !t.institutional && (!t.referenceOnly || cat === 'reference')
	);
};

const noticeTypeNeeds = (value, field) =>
	Boolean(noticeTypeConfig(value)?.fields?.includes(field));

const RECIPIENT_LABELS = {
	claimant: 'Claimant(s)',
	respondent: 'Respondent(s)',
	arbitrator: 'Arbitrator(s)',
};

const useStyles = makeStyles(() => ({
	tableCell: {
		padding: "10px",
		fontSize: "0.875rem",
		lineHeight: 1.6,
		verticalAlign: "top",
	},
	// Case Title and Sent To carry long unbroken strings that made rows very
	// tall. Clamp them to three lines; the full value stays available on hover
	// via the cell's title attribute.
	clampCell: {
		display: "-webkit-box",
		WebkitLineClamp: 3,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		overflowWrap: "break-word",
	},
	caseTitleCell: {
		minWidth: 160,
	},
	sentToCell: {
		minWidth: 140,
	},
	toolbar: {
		display: "flex",
		alignItems: "stretch",
		gap: "1rem",
		justifyContent: "space-between",
		marginBottom: "1rem",
	}
}));

const Notices = ({ classes: incomingClasses }) => {
	const localClasses = useStyles();

	// Screen switcher: 'list' or 'create'
	const [currentScreen, setCurrentScreen] = useState('list');
	const [notices, setNotices] = useState([]);
	const [totalCount, setTotalCount] = useState(0); // Holds the total count from API
	const [loadingList, setLoadingList] = useState(false);
	const [search, setSearch] = useState("");

	// Pagination States (Fixed at 10 items per page)
	const [page, setPage] = useState(0);
	const rowsPerPage = 10;

	// Form States for Creating a Notice
	const [searchedCase, setSearchedCase] = useState(null);
	const [selectedParties, setSelectedParties] = useState({ claimant: false, respondent: false });
	// An arbitrator notice is addressed to ONE arbitrator, so the arbitrator
	// recipient is held as that arbitrator's profile id rather than a boolean
	// (null = none selected). Exactly one recipient can be selected at a time,
	// so picking an arbitrator clears the party checkboxes and vice versa.
	const [selectedArbitratorId, setSelectedArbitratorId] = useState(null);
	const [noticeType, setNoticeType] = useState('');
	const [hearingDate, setHearingDate] = useState(null);
	const [listingTime, setListingTime] = useState('');
	// Download-only notices (MSME file closure) have no hearing date/time: the
	// inputs are hidden and any value left over from a previous type is ignored.
	const isDownloadOnly = Boolean(noticeTypeConfig(noticeType)?.downloadOnly);
	const [statementOfClaimFilingDate, setStatementOfClaimFilingDate] = useState(null);
	const [writtenResponseFilingDate, setWrittenResponseFilingDate] = useState(null);
	// Fee Schedule for the Written Response notice. Typed in by the admin
	const [fees, setFees] = useState({ arbitrator_fee: '', administrative_expense: '', sitting_fee: '' });
	const [submitting, setSubmitting] = useState(false);
	const [noticeError, setNoticeError] = useState('');

	// ---- Preview States ----
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewFileId, setPreviewFileId] = useState(null);
	const [previewData, setPreviewData] = useState(null);
	const [openPreviewModal, setOpenPreviewModal] = useState(false);

	// Fetch notices for main table
	const fetchNotices = async (searchText = search) => {
		try {
			setLoadingList(true);
			// Both endpoints are paginated the same way, so `page` has to travel
			// with the search as well -- without it every page of a search comes
			// back as page 1 while the pager still counts up.
			const url = searchText ? '/bac/search/notice/' : '/bac/notice/';
			const params = { page: page + 1 };
			if (searchText) params.search = searchText;

			const response = await instance.get(url, { params });
			const data = response.data;

			if (data && typeof data === 'object' && 'results' in data) {
				setNotices(data.results || []);
				setTotalCount(data.count || 0); // Read total count from backend response
			} else {
				setNotices(Array.isArray(data) ? data : []);
				setTotalCount(Array.isArray(data) ? data.length : 0);
			}
		} catch (err) {
			console.error("Failed to load notices:", err);
		} finally {
			setLoadingList(false);
		}
	};

	useEffect(() => {
		if (currentScreen === 'list') {
			fetchNotices();
		}
	}, [currentScreen, page, search]); // Re-fetch when page or search changes

	const handleSearchChange = (val) => {
		setSearch(val);
		setPage(0);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleSearchSuccess = (caseData) => {
		setSearchedCase(caseData);
		// A different case means the staged preview (which belongs to the old
		// case) must not be what Send dispatches.
		setPreviewFileId(null);
		// A notice type picked for the previous case may not be offered for a
		// case of a different category (institutional vs. CMP/Reference), so
		// clear it rather than leave a now-hidden type selected.
		setNoticeType('');
		const recipient = noticeTypeConfig(noticeType)?.recipient;
		setSelectedParties({
			claimant: recipient === 'claimant',
			respondent: recipient === 'respondent',
		});
		// Any previously picked arbitrator belongs to the old case.
		setSelectedArbitratorId(null);
	};

	// Selecting a notice type pre-selects the party its template is addressed
	// to. The checkboxes stay editable — this is a default, not a lock.
	const handleNoticeTypeChange = (value) => {
		setNoticeType(value);
		setNoticeError('');
		// Critical: previewFileId is sent as `file_id`, and the send path then
		// dispatches the STAGED document rather than regenerating. Without this
		// reset, previewing one notice type and then switching type would send
		// the previous type's document under the new type's name.
		setPreviewFileId(null);
		const recipient = noticeTypeConfig(value)?.recipient;
		if (recipient) {
			setSelectedParties({
				claimant: recipient === 'claimant',
				respondent: recipient === 'respondent',
			});
			// WHICH arbitrator cannot be defaulted - an arbitrator notice goes to
			// one arbitrator, so the admin has to name them.
			setSelectedArbitratorId(null);
		}
	};

	const handleFeeChange = (field) => (e) => {
		// Read the value out BEFORE calling setFees. React 16 pools synthetic
		// events and nulls their fields once the handler returns, and the updater
		// function runs after that — reading e.target.value inside it throws
		// "Cannot read properties of null".
		const { value } = e.target;
		if (!isAllowedAmountInput(value)) return;
		setFees((prev) => ({ ...prev, [field]: value }));
	};

	const handlePartyCheckboxChange = (e) => {
		const { name, checked } = e.target;
		if (checked) {
			setSelectedParties({
				claimant: name === "claimant",
				respondent: name === "respondent"
			});
			setSelectedArbitratorId(null);
		} else {
			setSelectedParties(prev => ({ ...prev, [name]: false }));
		}
	};

	// One arbitrator per notice: picking another replaces the current pick, and
	// clicking the selected one clears it.
	const handleArbitratorSelect = (arbitratorId) => {
		setSelectedParties({ claimant: false, respondent: false });
		setSelectedArbitratorId((prev) => (
			Number(prev) === Number(arbitratorId) ? null : arbitratorId
		));
	};


	const validateNoticeForm = () => {
		if (!searchedCase || !noticeType) {
			return "Please select a case and Type of Notice first.";
		}
		// Download-only notices (MSME file closure) have no hearing date/time and
		// no recipient — they are only previewed, edited and downloaded.
		if (noticeTypeConfig(noticeType)?.downloadOnly) {
			return "";
		}
		if (!hearingDate || !listingTime) {
			return "Please select Type of Notice, Hearing Date and Listing Time first.";
		}

		if (noticeTypeNeeds(noticeType, 'statementOfClaimFilingDate') && !statementOfClaimFilingDate) {
			return "Please select the Statement of Claim filing date first.";
		}

		if (noticeTypeNeeds(noticeType, 'writtenResponseFilingDate') && !writtenResponseFilingDate) {
			return "Please select the Written Response filing date first.";
		}

		if (!selectedParties.claimant && !selectedParties.respondent && !selectedArbitratorId) {
			return "Please select a recipient (Claimant(s), Respondent(s), or an Arbitrator).";
		}

		// The templates address a specific party — sending the Respondent's
		// Written Response notice to the Claimant would be a real mistake, not a
		// formatting one, so it is blocked rather than warned about.
		const expected = noticeTypeConfig(noticeType)?.recipient;
		if (expected === 'arbitrator') {
			if (!searchedCase.arbitrators || searchedCase.arbitrators.length === 0) {
				return "This case has no Arbitrator appointed.";
			}
			if (!selectedArbitratorId) {
				return "This notice is addressed to an Arbitrator — please select the arbitrator it should be sent to.";
			}
		} else if (expected && !selectedParties[expected]) {
			return `This notice is addressed to the ${RECIPIENT_LABELS[expected]} — please select that recipient.`;
		}

		if (selectedParties.claimant && (!searchedCase.claimants || searchedCase.claimants.length === 0) && (!searchedCase.claimant || !searchedCase.claimant.name || searchedCase.claimant.name === 'N/A')) {
			return "Selected recipient has no Claimant profile assigned.";
		}

		if (selectedParties.respondent && (!searchedCase.respondents || searchedCase.respondents.length === 0) && (!searchedCase.respondent || !searchedCase.respondent.name || searchedCase.respondent.name === 'N/A')) {
			return "Selected recipient has no Respondent profile assigned.";
		}

		return null;
	};

	// The per-type inputs, in one place, so Preview (GET) and Send (POST) can
	// never disagree about what the notice was built from.
	const noticeInputParams = () => {
		const params = {};
		// The arbitrator the notice is addressed to. Sent on the preview as well,
		// so the staged document (which Send then dispatches as-is) is the one
		// built for this arbitrator.
		if (selectedArbitratorId) {
			params.profile_id = selectedArbitratorId;
		}
		if (noticeTypeNeeds(noticeType, 'statementOfClaimFilingDate')) {
			params.statement_of_claim_filing_date = statementOfClaimFilingDate
				? format(statementOfClaimFilingDate, 'yyyy-MM-dd') : null;
		}
		if (noticeTypeNeeds(noticeType, 'writtenResponseFilingDate')) {
			params.written_response_filing_date = writtenResponseFilingDate
				? format(writtenResponseFilingDate, 'yyyy-MM-dd') : null;
		}
		if (noticeTypeNeeds(noticeType, 'fees')) {
			['arbitrator_fee', 'administrative_expense', 'sitting_fee'].forEach((field) => {
				if (fees[field] !== '' && fees[field] !== null && fees[field] !== undefined) {
					params[field] = fees[field];
				}
			});
		}
		return params;
	};

	const apiErrorMessage = (err) =>
		err?.response?.data?.error || err?.response?.data?.message || '';

	const handleCreateNoticeSubmit = async (e) => {
		e.preventDefault();
		if (!searchedCase || !noticeType) return;

		const validationError = validateNoticeForm();
		if (validationError) {
			setNoticeError(validationError);
			return;
		}
		setNoticeError('');

		const targetParties = [];
		if (selectedParties.claimant) targetParties.push("claimant");
		if (selectedParties.respondent) targetParties.push("respondent");
		if (selectedArbitratorId) targetParties.push("arbitrator");

		try {
			setSubmitting(true);

			const payload = {
				dispute_id: searchedCase.id,
				type: noticeType,
				listing_date: hearingDate ? format(hearingDate, 'yyyy-MM-dd') : null,
				meeting_date: hearingDate ? format(hearingDate, 'yyyy-MM-dd') : null,
				meeting_time: listingTime || null,
				sent_to: targetParties,
				party_type: targetParties?.[0],
				...noticeInputParams(),
			};

			if (previewFileId) {
				payload.file_id = previewFileId;
			}

			const response = await instance.post('/bac/notice/', payload, {
				suppressGlobalErrorHandling: true,
			});

			if (response.status === 200 || response.status === 201) {
				Swal.fire("Success", "Notice successfully sent!", "success");
				setSearchedCase(null);
				setSelectedParties({ claimant: false, respondent: false });
				setSelectedArbitratorId(null);
				setNoticeType('');
				setHearingDate(null);
				setListingTime('');
				setStatementOfClaimFilingDate(null);
				setWrittenResponseFilingDate(null);
				setFees({ arbitrator_fee: '', administrative_expense: '', sitting_fee: '' });
				setPreviewFileId(null);
				setPage(0);
				setCurrentScreen('list');
			} else {
				setNoticeError("Failed to submit notice.");
			}
		} catch (err) {
			console.error("Error creating notice:", err);
			setNoticeError(apiErrorMessage(err) || "An error occurred while creating the notice.");
		} finally {
			setSubmitting(false);
		}
	};

	const handlePreviewNoticeClick = async () => {
		const validationError = validateNoticeForm();
		if (validationError) {
			setNoticeError(validationError);
			return;
		}
		setNoticeError('');

		try {
			setPreviewLoading(true);
			// Download-only notices (MSME file closure) carry no hearing date;
			// guard the format() call and let the backend fall back.
			const dateStr = !isDownloadOnly && hearingDate ? format(hearingDate, 'yyyy-MM-dd') : null;
			const params = {
				dispute_id: searchedCase.id,
				listing_date: dateStr,
				notice_type: noticeType,
				meeting_date: dateStr,
				meeting_time: (!isDownloadOnly && listingTime) || null,
				...noticeInputParams(),
			}
			const res = await getPreviewNotice(params, previewFileId, {
				suppressGlobalErrorHandling: true,
			});

			const item = res?.data?.data?.[0] || res?.data?.[0];
			if (item) {
				const previewDownloadUrl = validateOrigin(item.download_url) || '';
				if (item.download_url && !previewDownloadUrl) {
					console.error(
						"Discarded an untrusted notice download URL (notice preview):",
						item.download_url
					);
				}
				setPreviewFileId(item.file_id);
				setPreviewData({
					fileId: item.file_id,
					downloadUrl: previewDownloadUrl,
					contentHtml: item.content_html || '',
					manuallyEdited: item.manually_edited || false,
					claimants: item.claimant_data || [],
					counsels: item.counsel_data || []
				});
				setOpenPreviewModal(true);
			} else {
				setNoticeError("Preview response format is invalid.");
			}
		} catch (err) {
			console.error("Error generating preview:", err);
			setNoticeError(apiErrorMessage(err) || "Failed to generate notice preview.");
		} finally {
			setPreviewLoading(false);
		}
	};

	// ---- Download handler ----
	const [downloadingId, setDownloadingId] = useState(null);

	const handleCaseNoClick = async (noticeId) => {
		if (downloadingId === noticeId) return;
		setDownloadingId(noticeId);
		try {
			const res = await instance.get(`/bac/notice/download/${noticeId}/`);
			const documents = res.data?.documents || [];
			const onError = (message) => Swal.fire("Error", message, "error");
			if (documents.length === 1) {
				navigateToTrustedUrl(documents[0].download_url, {
					onError,
					context: "notice download",
				});
			} else {
				documents.forEach((doc) => {
					downloadTrustedUrlInIframe(doc.download_url, {
						onError,
						context: "notice download",
					});
				});
			}
		} catch (err) {
			console.error("Failed to download notice documents:", err);
		} finally {
			setDownloadingId(null);
		}
	};

	const refNo = getCaseReferenceNumber(searchedCase);

	return (
		<Box>
			{currentScreen === 'list' ? (
				/* --- VIEW 1: NOTICES HISTORY LIST TABLE --- */
				<Box>
					<Box className={localClasses.toolbar}>
						<GlobalSearch
							value={search}
							onSearchChange={handleSearchChange}
							placeholder="Search Notices..."
							// minWidth 0 lets the search narrow on phones so the button
							// stays on the same row at full height
							style={{ width: "400px", minWidth: 0 }}
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={() => setCurrentScreen('create')}
							size="small"
							style={{ flexShrink: 0, whiteSpace: "nowrap" }}
						>
							Create Notice
						</Button>
					</Box>

					<TableContainer component={Paper}>
						<Table aria-label="notices dashboard table" style={{ maxWidth: '100%', marginTop: 0 }}>
							<TableHead style={{ backgroundColor: '#f5f5f5' }}>
								<TableRow>
									<TableCell className={localClasses.tableCell} style={{ minWidth: 120 }}><strong>Case No.</strong></TableCell>
									<TableCell className={`${localClasses.tableCell} ${localClasses.caseTitleCell}`}><strong>Case Title</strong></TableCell>
									<TableCell className={`${localClasses.tableCell} ${localClasses.sentToCell}`}><strong>Sent To</strong></TableCell>
									<TableCell className={localClasses.tableCell}><strong>Type of Notice</strong></TableCell>
									<TableCell className={localClasses.tableCell}><strong>Hearing Date</strong></TableCell>
									<TableCell className={localClasses.tableCell}><strong>Delivery Status</strong></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loadingList ? (
									<TableRow><TableCell colSpan={6} align="center" className={localClasses.tableCell}>Loading notices...</TableCell></TableRow>
								) : notices.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} align="center" className={localClasses.tableCell}>No records of notices sent found.</TableCell>
									</TableRow>
								) : (
									notices.map((notice, idx) => (
										<TableRow key={notice.id || idx} hover>
											<TableCell className={localClasses.tableCell} style={{ minWidth: 120 }}>
												<Link
													component="button"
													variant="body2"
													onClick={() => handleCaseNoClick(notice.id)}
													disabled={downloadingId === notice.id}
													style={{ cursor: downloadingId === notice.id ? "default" : "pointer", fontWeight: 500, fontSize: '0.875rem', textAlign: 'left' }}
												>
													{(notice.case_id || notice.tiac_custom_dispute_id || 'N/A')}
												</Link>
											</TableCell>
											<TableCell className={`${localClasses.tableCell} ${localClasses.caseTitleCell}`} title={notice.case_name || ''}>
												<div className={localClasses.clampCell}>
													{notice.case_name || 'N/A'}
												</div>
											</TableCell>
											<TableCell className={`${localClasses.tableCell} ${localClasses.sentToCell}`}>
												<div className={localClasses.clampCell}>
												{notice?.party_type === 'arbitrator' && (!notice?.party_names || notice.party_names.length === 0) ? (
													notice?.delivery_details && notice.delivery_details.length > 0 ? (
														notice.delivery_details.map((detail, dIdx) => (
															<div key={dIdx} style={{ display: 'block' }}>
																{detail.name || 'N/A'}
															</div>
														))
													) : (
														'N/A'
													)
												) : (
													formatPartyName(notice?.party_names) || 'N/A'
												)}
												</div>
											</TableCell>
											<TableCell className={localClasses.tableCell}>{notice?.type || notice.notice_type || 'N/A'}</TableCell>
											<TableCell className={localClasses.tableCell}>{notice.listing_date || 'N/A'}</TableCell>
											<TableCell className={localClasses.tableCell}>
												{notice.delivery_details && notice.delivery_details.length > 0 ? (
													notice.delivery_details.map((detail, dIdx) => (
														<Box
															key={dIdx}
															style={{
																padding: "4px 0",
																borderBottom: dIdx < notice.delivery_details.length - 1 ? "1px solid #eaeaea" : "none"
															}}
														>
															<Typography variant="body2" style={{ fontWeight: 500, fontSize: "0.8rem" }}>
																{displayEmail(detail.email)}
															</Typography>
															<Box display="flex" alignItems="center" gap={1} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
																<span
																	style={{
																		fontSize: "0.75rem",
																		fontWeight: "bold",
																		color: detail.status === "delivered" ? "#2e7d32" : detail.status === "dropped" ? "#d32f2f" : "#ed6c02",
																		textTransform: "capitalize"
																	}}
																>
																	{detail.status || "N/A"}
																</span>
																{detail.date && (
																	<span style={{ fontSize: "0.75rem", color: TEXT.muted }}>
																		({detail.date}, {safeDateFormat(detail.time, 'hh:mm a')})
																	</span>
																)}
															</Box>
														</Box>
													))
												) : (
													notice.delivered_date_time || notice.delivered_time || 'Pending'
												)}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>

						{/* Pagination component with single rowsPerPage option hide rowsPerPage selector dropdown */}
						{!loadingList && totalCount > 0 && (
							<TablePagination
								rowsPerPageOptions={[10]}
								component="div"
								count={totalCount}
								rowsPerPage={rowsPerPage}
								page={page}
								onChangePage={handleChangePage}
								// Handled to suppress any v4 select template layout glitches
								onChangeRowsPerPage={() => { }}
							/>
						)}
					</TableContainer>
				</Box>
			) : (
				/* --- VIEW 2: CREATE NEW NOTICE SCREEN --- */
				<Box>
					<Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: 16 }}>
						<Link
							color="inherit"
							href="#"
							onClick={(e) => { e.preventDefault(); setCurrentScreen('list'); }}
						>
							Notices
						</Link>
						<Typography color="textPrimary">Create Notice</Typography>
					</Breadcrumbs>

					<Box mb={3}>
						<SearchCaseForm
							classes={incomingClasses}
							handleSearchSuccess={handleSearchSuccess}
						/>
					</Box>

					{searchedCase && (
						<Paper style={{ padding: 24, marginTop: 16, textAlign: "left" }}>
							<Typography variant="h6" color="primary" gutterBottom={!refNo} style={{ textAlign: "left" }}>
								{searchedCase.tiac_custom_dispute_id}
							</Typography>
							{refNo && (
								<Typography variant="body2" color="textSecondary" gutterBottom style={{ textAlign: "left", overflowWrap: "anywhere" }}>
									{refNo.label}: {refNo.value}
								</Typography>
							)}
							<form onSubmit={handleCreateNoticeSubmit}>
								<Grid container spacing={3}>
									<Grid item xs={12} style={{ paddingBottom: '4rem' }}>
										<Typography style={{ marginBottom: 8, textAlign: "center" }}>
											Select Recipient
										</Typography>
										<Grid container spacing={2}>
											{/* Arbitrator Card. Each arbitrator is noticed
												individually, so every one of them carries its own
												checkbox and only one can be picked. */}
											<Grid item xs={12} sm={12}>
												<Box
													bgcolor="#fafafa"
													p={2}
													borderRadius={4}
													border={selectedArbitratorId ? "1.5px solid #61386d" : "1px solid #e0e0e0"}
													display="flex"
													flexDirection="column"
													alignItems="flex-start"
													style={{
														transition: "all 0.2s ease",
														textAlign: "left"
													}}
												>
													<Typography style={{ color: TEXT.primary, fontWeight: "600" }}>Arbitrator(s)</Typography>
													<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
														The notice goes to one arbitrator at a time — select the arbitrator it is addressed to.
													</Typography>
													<Box mt={1} display="flex" flexDirection="column" alignItems="flex-start" style={{ textAlign: "left" }}>
														{searchedCase.arbitrators && searchedCase.arbitrators.length > 0 ? (
															searchedCase.arbitrators.map((a) => (
																<FormControlLabel
																	key={a.id}
																	control={
																		<Checkbox
																			checked={Number(selectedArbitratorId) === Number(a.id)}
																			onChange={() => handleArbitratorSelect(a.id)}
																			name={`arbitrator-${a.id}`}
																			color="primary"
																		/>
																	}
																	label={
																		<Typography color="textSecondary" style={{ textAlign: "left" }}>
																			{a.name} {a.is_presiding ? "(Presiding)" : ""}
																		</Typography>
																	}
																/>
															))
														) : (
															<Typography color="textSecondary" style={{ margin: "2px 0", textAlign: "left" }}>
																No Arbitrators appointed
															</Typography>
														)}
													</Box>
												</Box>
											</Grid>
											{/* Claimant Card */}
											<Grid item xs={12} sm={6}>
												<Box
													bgcolor="#fafafa"
													p={2}
													borderRadius={4}
													border={selectedParties.claimant ? "1.5px solid #61386d" : "1px solid #e0e0e0"}
													display="flex"
													flexDirection="column"
													alignItems="flex-start"
													height={"100%"}
													style={{
														cursor: "pointer",
														transition: "all 0.2s ease",
														textAlign: "left"
													}}
													onClick={() => {
														setSelectedParties({
															claimant: !selectedParties.claimant,
															respondent: false
														});
														setSelectedArbitratorId(null);
													}}
												>
													<FormControlLabel
														control={
															<Checkbox
																checked={selectedParties.claimant}
																onChange={(e) => {
																	e.stopPropagation();
																	handlePartyCheckboxChange(e);
																}}
																name="claimant"
																color="primary"
															/>
														}
														label={<strong style={{ color: TEXT.primary }}>Claimant(s)</strong>}
														onClick={(e) => e.stopPropagation()}
													/>
													<Box pl={3} mt={1} display="flex" flexDirection="column" alignItems="flex-start" style={{ textAlign: "left" }}>
														{searchedCase.claimants && searchedCase.claimants.length > 0 ? (
															searchedCase.claimants.map((c) => (
																<Typography key={c.id} color="textSecondary" style={{ margin: "2px 0", textAlign: "left" }}>
																	• {c.name} ({displayEmail(c.email)})
																</Typography>
															))
														) : (
															<Typography variant="body2" color="textSecondary" style={{ margin: "2px 0", textAlign: "left" }}>
																• {searchedCase.claimant?.name || 'N/A'} ({displayEmail(searchedCase.claimant?.email)})
															</Typography>
														)}
													</Box>
												</Box>
											</Grid>

											{/* Respondent Card */}
											<Grid item xs={12} sm={6}>
												<Box
													bgcolor="#fafafa"
													p={2}
													borderRadius={4}
													border={selectedParties.respondent ? "1.5px solid #61386d" : "1px solid #e0e0e0"}
													display="flex"
													flexDirection="column"
													alignItems="flex-start"
													height={"100%"}
													style={{
														cursor: "pointer",
														transition: "all 0.2s ease",
														textAlign: "left"
													}}
													onClick={() => {
														setSelectedParties({
															claimant: false,
															respondent: !selectedParties.respondent
														});
														setSelectedArbitratorId(null);
													}}
												>
													<FormControlLabel
														control={
															<Checkbox
																checked={selectedParties.respondent}
																onChange={(e) => {
																	e.stopPropagation();
																	handlePartyCheckboxChange(e);
																}}
																name="respondent"
																color="primary"
															/>
														}
														label={<strong style={{ color: TEXT.primary }}>Respondent(s)</strong>}
														onClick={(e) => e.stopPropagation()}
													/>
													<Box pl={3} mt={1} display="flex" flexDirection="column" alignItems="flex-start" style={{ textAlign: "left" }}>
														{searchedCase.respondents && searchedCase.respondents.length > 0 ? (
															searchedCase.respondents.map((r) => (
																<Typography key={r.id} color="textSecondary" style={{ margin: "2px 0", textAlign: "left" }}>
																	• {r.name} ({displayEmail(r.email)})
																</Typography>
															))
														) : (
															<Typography variant="body2" color="textSecondary" style={{ margin: "2px 0", textAlign: "left" }}>
																• {searchedCase.respondent?.name || 'N/A'} ({displayEmail(searchedCase.respondent?.email)})
															</Typography>
														)}
													</Box>
												</Box>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={12} sm={4}>
										<FormControl size="small" fullWidth variant="outlined" required>
											<InputLabel id="notice-type-label">Type of Notice</InputLabel>
											<Select
												labelId="notice-type-label"
												label="Type of Notice"
												value={noticeType}
												onChange={(e) => handleNoticeTypeChange(e.target.value)}
											>
												{/* Institutional cases show only the two institutional
													respondent notices; all others show the standard four. */}
												{noticeTypesForCategory(searchedCase?.category).map((type) => (
													<MenuItem key={type.value} value={type.value}>
														{type.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>

									{noticeTypeNeeds(noticeType, 'statementOfClaimFilingDate') && (
										<Grid item xs={12} sm={4}>
											<DatePickerACCB
												label="Statement of Claim filing date"
												value={statementOfClaimFilingDate}
												onChange={setStatementOfClaimFilingDate}
												required
											/>
										</Grid>
									)}

									{noticeTypeNeeds(noticeType, 'writtenResponseFilingDate') && (
										<Grid item xs={12} sm={4}>
											<DatePickerACCB
												label="Written Response filing date"
												value={writtenResponseFilingDate}
												onChange={setWrittenResponseFilingDate}
												required
											/>
										</Grid>
									)}

									{!isDownloadOnly && (
										<>
											<Grid item xs={12} sm={4}>
												<DatePickerACCB
													label="Hearing Date"
													value={hearingDate}
													onChange={setHearingDate}
													required
												/>
											</Grid>

											<Grid item xs={12} sm={4}>
												<TextField
													required
													label="Listing Time"
													variant="outlined"
													size="small"
													fullWidth
													value={listingTime}
													onChange={(e) => setListingTime(e.target.value)}
													placeholder="e.g. 11:00 AM"
												/>
											</Grid>
										</>
									)}

									{noticeTypeNeeds(noticeType, 'fees') && (
										<Grid item xs={12}>
											<Box
												bgcolor="#fafafa"
												p={2}
												borderRadius={4}
												border="1px solid #e0e0e0"
												style={{ textAlign: 'left' }}
											>
												<Typography variant="subtitle2" style={{ marginBottom: 4 }}>
													Fee Schedule
												</Typography>
												<Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
													Please enter the entire fee for the matter. We will calculate Respondent(s) share for you. 
												</Typography>
												<Grid container spacing={2}>
													<Grid item xs={12} sm={4}>
														<TextField
															label="Arbitrator's Fee (total)"
															variant="outlined"
															size="small"
															fullWidth
															type="number"
															value={fees.arbitrator_fee}
															onChange={handleFeeChange('arbitrator_fee')}
															helperText=""
														/>
													</Grid>
													<Grid item xs={12} sm={4}>
														<TextField
															label="Administrative Expenses (total)"
															variant="outlined"
															size="small"
															fullWidth
															type="number"
															value={fees.administrative_expense}
															onChange={handleFeeChange('administrative_expense')}
															helperText=""
														/>
													</Grid>
													<Grid item xs={12} sm={4}>
														<TextField
															label="Tentative sittings Fee (total)"
															variant="outlined"
															size="small"
															fullWidth
															type="number"
															value={fees.sitting_fee}
															onChange={handleFeeChange('sitting_fee')}
															helperText=""
														/>
													</Grid>
												</Grid>
											</Box>
										</Grid>
									)}

									<Grid item xs={12}>
										<Box display="flex" gap={2} style={{ display: 'flex', gap: '2rem' }}>
											{/* Every notice type can be previewed and edited now — the
												preview path is template-agnostic (see PreviewNoticeView). */}
											{Boolean(noticeType) && (
												<Button
													variant="contained"
													color="primary"
													size="large"
													disabled={submitting || previewLoading}
													onClick={handlePreviewNoticeClick}
												>
													{previewLoading ? "Loading Preview..." : "Preview Notice"}
												</Button>
											)}
											{/* Download-only notices (MSME file closure) are never sent —
												preview, edit and download only, so the Send button is hidden. */}
											{!noticeTypeConfig(noticeType)?.downloadOnly && (
												<Button
													type="submit"
													variant="contained"
													color="primary"
													size="large"
													disabled={submitting}
												>
													{submitting ? "Sending..." : "Send Notice"}
												</Button>
											)}
											<Button
												variant="outlined"
												size="large"
												onClick={() => {
													setPreviewFileId(null);
													setCurrentScreen('list');
												}}
											>
												Cancel
											</Button>
										</Box>
										<FormError message={noticeError} />
									</Grid>
								</Grid>
							</form>
						</Paper>
					)}
				</Box>
			)}
			{openPreviewModal && (
				<NoticePreview
					open={openPreviewModal}
					onClose={() => setOpenPreviewModal(false)}
					previewData={previewData}
					disputeId={searchedCase?.id}
					caseNumber={searchedCase?.tiac_custom_dispute_id}
					listingDate={!isDownloadOnly && hearingDate ? format(hearingDate, 'yyyy-MM-dd') : null}
					noticeType={noticeType}
					noticeInputParams={noticeInputParams()}
					onUpdateSuccess={(newFileId) => setPreviewFileId(newFileId)}
				/>
			)}
		</Box>
	);
};

export default Notices;